import { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2 } from 'lucide-react';

interface AutocompleteOption {
  value: string;
  label: string;
  sublabel?: string;
  data?: any;
}

interface AutocompleteProps {
  label: string;
  placeholder?: string;
  value: string;
  onValueChange: (value: string) => void;
  onSelect: (option: AutocompleteOption) => void;
  onSearch: (query: string) => Promise<AutocompleteOption[]>;
  minSearchLength?: number;
  debounceMs?: number;
  helperText?: string;
  required?: boolean;
  error?: string;
}

export default function Autocomplete({
  label,
  placeholder,
  value,
  onValueChange,
  onSelect,
  onSearch,
  minSearchLength = 4,
  debounceMs = 300,
  helperText,
  required,
  error,
}: AutocompleteProps) {
  const [inputValue, setInputValue] = useState(value);
  const [options, setOptions] = useState<AutocompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimer = useRef<NodeJS.Timeout>();

  // Update input value when prop value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Debounced search
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (inputValue.length >= minSearchLength) {
      debounceTimer.current = setTimeout(async () => {
        setIsLoading(true);
        try {
          const results = await onSearch(inputValue);
          setOptions(results);
          setIsOpen(true);
        } catch (error) {
          console.error('Search error:', error);
          setOptions([]);
        } finally {
          setIsLoading(false);
        }
      }, debounceMs);
    } else {
      setOptions([]);
      setIsOpen(false);
    }

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [inputValue, minSearchLength, debounceMs, onSearch]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onValueChange(newValue);
    setSelectedIndex(-1);
  };

  const handleSelect = (option: AutocompleteOption) => {
    setInputValue(option.label);
    onValueChange(option.label);
    onSelect(option);
    setIsOpen(false);
    setOptions([]);
  };

  const handleClear = () => {
    setInputValue('');
    onValueChange('');
    setOptions([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || options.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < options.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < options.length) {
          handleSelect(options[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <label className="block text-sm font-medium text-neutral-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {isLoading ? (
            <Loader2 className="h-5 w-5 text-neutral-400 animate-spin" />
          ) : (
            <Search className="h-5 w-5 text-neutral-400" />
          )}
        </div>

        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (options.length > 0) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          className={`
            block w-full pl-10 pr-10 py-2.5 text-base
            border ${error ? 'border-red-500' : 'border-neutral-300'}
            rounded-lg shadow-sm
            placeholder-neutral-400
            focus:outline-none focus:ring-2
            ${error ? 'focus:ring-red-500 focus:border-red-500' : 'focus:ring-secondary-500 focus:border-secondary-500'}
            transition-colors duration-200
          `}
        />

        {inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-neutral-400 hover:text-neutral-600"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {helperText && !error && (
        <p className="mt-1 text-sm text-neutral-500">{helperText}</p>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-500">{error}</p>
      )}

      {inputValue.length > 0 && inputValue.length < minSearchLength && (
        <p className="mt-1 text-sm text-neutral-500">
          Type at least {minSearchLength} characters to search
        </p>
      )}

      {/* Dropdown */}
      {isOpen && options.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {options.map((option, index) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option)}
              className={`
                w-full px-4 py-3 text-left hover:bg-secondary-50 transition-colors
                ${index === selectedIndex ? 'bg-secondary-50' : ''}
                ${index > 0 ? 'border-t border-neutral-100' : ''}
              `}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-900 truncate">
                    {option.label}
                  </p>
                  {option.sublabel && (
                    <p className="text-sm text-neutral-500 truncate">
                      {option.sublabel}
                    </p>
                  )}
                </div>
                {option.data?.ticker && (
                  <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800">
                    {option.data.ticker}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {isOpen && !isLoading && options.length === 0 && inputValue.length >= minSearchLength && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg p-4 text-center">
          <p className="text-sm text-neutral-500">No companies found</p>
        </div>
      )}
    </div>
  );
}
